import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Card, CardContent } from '../../components/ui/card';
import { Label } from '../../components/ui/label';
import { Separator } from '../../components/ui/separator';
import { Badge } from '../../components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../../components/ui/dialog';
import {
  ChevronLeft,
  Plus,
  Trash2,
  Image as ImageIcon,
  Eye,
  AlertCircle,
} from 'lucide-react';
import { getHeroCarouselItems, createHeroCarouselItem, updateHeroCarouselItem, deleteHeroCarouselItem } from '../../api';

// Placeholder images for quick selection
const PLACEHOLDER_IMAGES = [
  'https://images.unsplash.com/photo-1558618666-fcd22c43c639?w=800&h=400&fit=crop',
  'https://images.unsplash.com/photo-1558679908-310be1c334c3?w=800&h=400&fit=crop',
  'https://images.unsplash.com/photo-1590424677278-fa2b8e80e2a7?w=800&h=400&fit=crop',
  'https://images.unsplash.com/photo-1568772585704-748b57f9af90?w=800&h=400&fit=crop',
  'https://images.unsplash.com/photo-1525160354320-d8e92641c563?w=800&h=400&fit=crop',
  'https://images.unsplash.com/photo-1609630875171-b1321377ee65?w=800&h=400&fit=crop',
];

export default function CarouselAdmin() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openDialog, setOpenDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [previewUrl, setPreviewUrl] = useState('');

  // Form state for new/editing item
  const [form, setForm] = useState({
    title: '',
    subtitle: '',
    image: '',
    link: '/shop',
    order: 0,
  });

  useEffect(() => {
    setLoading(true);
    getHeroCarouselItems()
      .then(setItems)
      .catch(() => setError('Failed to load carousel items'))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!form.title.trim() || !form.image.trim()) {
      setError('Title and image are required');
      return;
    }
    setError('');

    try {
      if (editingItem) {
        await updateHeroCarouselItem(editingItem._id, form);
      } else {
        await createHeroCarouselItem(form);
      }
      setOpenDialog(false);
      setForm({ title: '', subtitle: '', image: '', link: '/shop', order: 0 });
      setEditingItem(null);
      // Reload items
      const itemsData = await getHeroCarouselItems();
      setItems(itemsData);
    } catch {
      setError('Save failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this carousel item?')) return;
    try {
      await deleteHeroCarouselItem(id);
      setItems(items.filter(i => i._id !== id));
    } catch {
      setError('Delete failed');
    }
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setForm({
      title: item.title || '',
      subtitle: item.subtitle || '',
      image: item.image || '',
      link: item.link || '/shop',
      order: item.order || 0,
    });
    setOpenDialog(true);
  };

  const openNew = () => {
    setEditingItem(null);
    setForm({ title: '', subtitle: '', image: '', link: '/shop', order: items.length });
    setOpenDialog(true);
  };

  if (loading) return <div className="flex items-center justify-center min-h-screen"><p>Loading...</p></div>;

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin')}>
            <ChevronLeft className="w-4 h-4 mr-1" />
            Back
          </Button>
          <h1 className="text-2xl font-bold text-foreground">Hero Carousel</h1>
        </div>
        <Button onClick={openNew}>
          <Plus className="w-4 h-4 mr-2" />
          Add Slide
        </Button>
      </div>

      {error && (
        <div className="flex items-center gap-2 p-3 mb-4 bg-red-50 border border-red-200 text-red-700 rounded-lg">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {items.length === 0 ? (
        <Card className="border-dashed">
          <CardContent className="flex flex-col items-center justify-center py-12">
            <ImageIcon className="w-12 h-12 text-muted-foreground mb-4" />
            <p className="text-muted-foreground mb-4">No carousel items yet.</p>
            <Button onClick={openNew}>Add First Slide</Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {items
            .sort((a, b) => (a.order || 0) - (b.order || 0))
            .map((item, index) => (
              <Card key={item._id} className="overflow-hidden border-border">
                {/* Preview Image */}
                <div className="relative h-48 bg-muted/30">
                  <img
                    src={item.image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-full h-full items-center justify-center text-muted-foreground">
                    <ImageIcon className="w-8 h-8" />
                  </div>
                  <Badge className="absolute top-2 left-2 bg-foreground text-background">
                    Order: {item.order || index}
                  </Badge>
                </div>

                <CardContent className="p-4">
                  <h3 className="font-semibold text-foreground mb-1 line-clamp-1">{item.title}</h3>
                  <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{item.subtitle}</p>
                  <p className="text-xs text-muted-foreground mb-3">
                    Link: {item.link}
                  </p>
                  <Separator className="my-3" />
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => setPreviewUrl(item.image)}
                    >
                      <Eye className="w-4 h-4 mr-1" />
                      Preview
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      className="flex-1"
                      onClick={() => openEdit(item)}
                    >
                      Edit
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => handleDelete(item._id)}
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
        </div>
      )}

      {/* Add/Edit Dialog */}
      <Dialog open={openDialog} onOpenChange={setOpenDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Edit Slide' : 'Add New Slide'}</DialogTitle>
            <DialogDescription>
              {editingItem ? 'Update the carousel slide.' : 'Create a new hero carousel slide.'}
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Image URL */}
            <div className="space-y-2">
              <Label>Image URL *</Label>
              <Input
                value={form.image}
                onChange={(e) => setForm({ ...form, image: e.target.value })}
                placeholder="https://example.com/image.jpg"
              />
              {form.image && (
                <div className="relative h-40 bg-muted/30 rounded-md overflow-hidden">
                  <img
                    src={form.image}
                    alt="Preview"
                    className="w-full h-full object-cover"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'flex';
                    }}
                  />
                  <div className="hidden w-full h-full items-center justify-center text-muted-foreground">
                    <ImageIcon className="w-8 h-8" />
                  </div>
                </div>
              )}
            </div>

            {/* Quick select placeholder images */}
            <div className="space-y-2">
              <Label className="text-xs">Quick Select Placeholders</Label>
              <div className="grid grid-cols-3 gap-2">
                {PLACEHOLDER_IMAGES.map((img, i) => (
                  <button
                    key={i}
                    type="button"
                    onClick={() => setForm({ ...form, image: img })}
                    className={`relative h-20 rounded-md overflow-hidden border-2 transition-colors ${
                      form.image === img ? 'border-brand-red' : 'border-border hover:border-foreground'
                    }`}
                  >
                    <img src={img} alt="" className="w-full h-full object-cover" />
                  </button>
                ))}
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="Main heading"
                />
              </div>
              <div className="space-y-2">
                <Label>Subtitle</Label>
                <Input
                  value={form.subtitle}
                  onChange={(e) => setForm({ ...form, subtitle: e.target.value })}
                  placeholder="Sub heading"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Link URL</Label>
              <Input
                value={form.link}
                onChange={(e) => setForm({ ...form, link: e.target.value })}
                placeholder="/shop"
              />
            </div>

            <div className="space-y-2">
              <Label>Order</Label>
              <Input
                type="number"
                value={form.order}
                onChange={(e) => setForm({ ...form, order: parseInt(e.target.value) || 0 })}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpenDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleSave}>
              {editingItem ? 'Update' : 'Create'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={!!previewUrl} onOpenChange={(open) => !open && setPreviewUrl('')}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle>Image Preview</DialogTitle>
          </DialogHeader>
          {previewUrl && (
            <img src={previewUrl} alt="Preview" className="w-full rounded-md" />
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}